import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, ArrowUpRight, Building2, Hotel, Map, Presentation, Plane } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { resolveImage } from "../admin/ImageUpload";

/**
 * Şirketlerimiz — compact 2-column grid.
 * Each card: cover image (left) + minimal content (right):
 *   logo, sector chip, short description (2 lines), website link.
 */

const ICONS = {
  "firat-real-estate": Building2,
  hotelika: Hotel,
  "e-ipat": Map,
  "arsa-yatirim-zirvesi": Presentation,
  "vgo-plus": Plane,
};

const CompanyCard = ({ c, i }) => {
  const { pick } = useI18n();
  const Icon = ICONS[c.slug] || Building2;
  const hostname = (c.website_url || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
  const External = !!c.website_url;

  const inner = (
    <div className="grid grid-cols-[110px_1fr] md:grid-cols-[140px_1fr] h-full">
      {/* Cover thumbnail */}
      <div className="relative overflow-hidden bg-brand-primary/20">
        <img
          src={resolveImage(c.cover_image)}
          alt={pick(c.name)}
          className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[1000ms] group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy-deep/50 to-transparent" />
        <div className="absolute top-2 left-2">
          <span className="w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 text-white">
            <Icon size={12} strokeWidth={1.5} />
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 md:p-5 bg-white flex flex-col min-w-0">
        {c.logo ? (
          <img
            src={resolveImage(c.logo)}
            alt={pick(c.name)}
            className="h-7 md:h-8 w-auto max-w-[130px] object-contain object-left mb-2"
            loading="lazy"
          />
        ) : (
          <h3 className="font-serif text-brand-navy text-base md:text-lg leading-tight mb-2">
            {pick(c.name)}
          </h3>
        )}

        <div className="text-[0.55rem] md:text-[0.6rem] tracking-[0.22em] uppercase text-brand-primary font-medium mb-1.5 line-clamp-1">
          {pick(c.sector) || pick(c.tagline)}
        </div>

        <p className="text-[0.78rem] md:text-[0.82rem] text-brand-dark leading-[1.55] line-clamp-2 md:line-clamp-3 flex-1">
          {pick(c.description)}
        </p>

        <div className="mt-3 pt-3 flex items-center justify-between border-t border-black/10 gap-2">
          {hostname ? (
            <span className="text-[0.62rem] tracking-[0.14em] uppercase text-brand-medium truncate min-w-0">
              {hostname}
            </span>
          ) : (
            <span />
          )}
          <span className="inline-flex items-center gap-1 text-[0.62rem] tracking-[0.2em] uppercase text-brand-navy group-hover:text-brand-primary transition-colors flex-shrink-0">
            Ziyaret
            <ArrowUpRight
              size={12}
              strokeWidth={1.5}
              className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
            {External ? <ExternalLink size={10} strokeWidth={1.5} className="opacity-60" /> : null}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: i * 0.06, ease: [0.19, 1, 0.22, 1] }}
      className="relative bg-white h-full"
    >
      {External ? (
        <a
          href={c.website_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block group h-full"
          data-testid={`group-company-${c.slug}`}
        >
          {inner}
        </a>
      ) : (
        <div className="block group h-full" data-testid={`group-company-${c.slug}`}>
          {inner}
        </div>
      )}
    </motion.article>
  );
};

const GroupCompaniesSection = ({ companies }) => {
  const { pick } = useI18n();
  if (!companies?.length) return null;

  return (
    <section className="bg-brand-navy text-white relative overflow-hidden" data-testid="group-companies-section">
      <div className="grain absolute inset-0 pointer-events-none opacity-40" />
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-primary-bright/10 pointer-events-none" />

      <div className="container-x py-10 md:py-14 relative">
        <div className="max-w-3xl mb-8 md:mb-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-10 bg-brand-primary-bright" />
            <span className="text-[0.66rem] tracking-[0.36em] uppercase text-brand-primary-bright font-medium">
              Fırat Group
            </span>
          </div>
          <h2
            className="font-serif text-white leading-[1.05] tracking-tight"
            style={{ fontSize: "clamp(1.75rem, 3.6vw, 3rem)" }}
          >
            {pick({
              tr: "Şirketlerimiz",
              en: "Our Companies",
              ar: "شركاتنا",
            })}
          </h2>
          <p className="mt-4 text-white/70 max-w-2xl text-sm md:text-base leading-relaxed">
            {pick({
              tr: "Gayrimenkul geliştirme, uluslararası otel yatırımları, PropTech ve turizmden sektörel etkinliklere — birbirini besleyen entegre bir ekosistem inşa ediyoruz. Farklı sektörlerde de aktifiz; her biri, gruba değer katan ve ortak vizyonumuzu genişleten önemli bir parçadır.",
              en: "From real-estate development and international hotel investments to PropTech, travel and industry events — we build an integrated ecosystem where each sector reinforces the next. We are active in adjacent sectors too; each one is a valuable extension of our shared vision.",
              ar: "من التطوير العقاري إلى الاستثمار الفندقي الدولي والسياحة وتقنيات العقارات — نبني نظاماً بيئياً متكاملاً.",
            })}
          </p>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4"
          data-testid="group-companies-grid"
        >
          {companies.slice(0, 8).map((c, i) => (
            <CompanyCard key={c.id || c.slug} c={c} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default GroupCompaniesSection;
